import React from 'react';
import { Container, Box, Typography, Button, FormControlLabel, RadioGroup, Radio } from '@mui/material';
import { PurchaseReport } from '../components/PurchaseReport';
import { api } from '../api';
import { Item } from '../types';

interface ReportPageProps {
  selectedDate: string;
  onBack: () => void;
}

type PurchaseData = {
  date: string;
  customerName: string;
  customerAddress?: string;
  customerCCCD?: string;
  items: Item[];
  totalAmount: number;
};

export const ReportPage: React.FC<ReportPageProps> = ({ selectedDate }) => {
  const [purchases, setPurchases] = React.useState<PurchaseData[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [reportMode, setReportMode] = React.useState<'separate' | 'combined'>('separate');

  React.useEffect(() => {
    loadReportData();
  }, [selectedDate]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const purchasesData = await api.getPurchases(selectedDate);
      const transformed = purchasesData.map((p) => ({
        date: p.date,
        customerName: p.customer?.name || 'N/A',
        customerAddress: p.customer?.address || '',
        customerCCCD: p.customer?.cccd || '',
        items: p.items || [],
        totalAmount: p.items?.reduce((sum, item) => sum + (item.total || 0), 0) || 0,
      }));
      setPurchases(transformed);
    } catch (err) {
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 150px)', '@media print': { p: 0} }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1,
          bgcolor: 'background.default',
          flexShrink: 0,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        </Typography>
        <RadioGroup
          row
          value={reportMode}
          onChange={(e) => setReportMode(e.target.value as 'separate' | 'combined')}
        >
          <FormControlLabel
            value="separate"
            control={<Radio />}
            label="Tách riêng mỗi khách hàng"
          />
          <FormControlLabel
            value="combined"
            control={<Radio />}
            label="Gom chung tất cả khách hàng"
          />
        </RadioGroup>
        <Button
          variant="contained"
          color="primary"
          startIcon={"🖨️"}
          onClick={handlePrint}
        >
          In Báo Cáo
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2, '@media print': { overflow: 'visible', px: 0, py: 0 } }}>
        <Box sx={{ maxWidth: 'lg', mx: 'auto', '@media print': { maxWidth: '100%' } }}>
          {loading ? (
            <Typography>Đang tải dữ liệu...</Typography>
          ) : (
            <PurchaseReport
              purchases={purchases}
              mode={reportMode}
              date={new Date(selectedDate)}
            />
          )}
        </Box>
      </Box>
    </Container>
  );
};
